import { Lock } from 'lucide-react';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { FormAccordionCard, FormSectionHeader } from '@/components/molecules';
import { EstimateAttachments } from '@/components/organisms/estimates/EstimateAttachments';
import type { Attachment } from '@/types/attachment';

interface EstimateInternalContentSectionProps {
  open: boolean;
  onToggle: () => void;
  bare?: boolean;
  internalNotes: string;
  onInternalNotesChange: (value: string) => void;
  isEditMode: boolean;
  editId?: string;
  pendingAttachmentId: string;
  internalAttachments: Attachment[];
  onInternalAttachmentsChange: (attachments: Attachment[]) => void;
}

export function EstimateInternalContentSection({
  open,
  onToggle,
  bare = false,
  internalNotes,
  onInternalNotesChange,
  isEditMode,
  editId,
  pendingAttachmentId,
  internalAttachments,
  onInternalAttachmentsChange,
}: EstimateInternalContentSectionProps) {
  const body = (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="internal-notes" className="text-sm">
          Notes{' '}
          <span className="text-xs text-muted-foreground">(private, not shown to customer)</span>
        </Label>
        <Textarea
          id="internal-notes"
          placeholder="Add internal notes for your team..."
          value={internalNotes}
          onChange={(event) => onInternalNotesChange(event.target.value)}
          rows={3}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">
          Attachments{' '}
          <span className="text-xs text-muted-foreground">(private, not shown to customer)</span>
        </Label>
        {isEditMode && editId ? (
          <EstimateAttachments
            estimateId={editId}
            attachments={internalAttachments}
            onAttachmentsChange={onInternalAttachmentsChange}
            isInternal
          />
        ) : (
          <EstimateAttachments
            pendingId={pendingAttachmentId}
            attachments={internalAttachments}
            onAttachmentsChange={onInternalAttachmentsChange}
            isInternal
          />
        )}
      </div>
    </>
  );

  if (bare) {
    return <div className="space-y-4">{body}</div>;
  }

  return (
    <FormAccordionCard
      open={open}
      header={
        <FormSectionHeader
          icon={Lock}
          title="For internal"
          open={open}
          onToggle={onToggle}
          statusBadge={internalNotes ? 'Added' : undefined}
        />
      }
      bodyClassName="space-y-4"
    >
      {body}
    </FormAccordionCard>
  );
}
